/** Time Management: the vocabulary the pages share, and the rules they can answer without asking the server. */
import { ALREADY_APPROVED, COSIGN_REQUIRED } from '../../lib/timeCodes'
import { addDays } from './dates'
import { downtimeOffProject, type ProductTypes } from './downtimeProject'
import type {
  DayLimits,
  ApiBalance,
  ApiDay,
  ApiDayWindow,
  ApiLeave,
  ApiLogLine,
  CosignStage,
  CosignVerdict,
  DayClock as ApiDayClock,
  TimePerson,
} from '../../lib/time'

export type LogRow = ApiLogLine
export type DayLog = ApiDay
export type LeaveApplication = ApiLeave
export type Person = TimePerson
export type DayWindow = ApiDayWindow
export type DayClock = ApiDayClock

export const WORK_MODES = [
  'B - Office',
  'H - Home',
  'R - Remote',
  'F - Field',
  'BTA - Business Trip Abroad',
  'BTL - Business Trip Local',
  'T - Transit',
  'O - Off',
  'L - Leave',
  'Holiday',
] as const

export const NON_WORKING_MODES: string[] = ['O - Off', 'L - Leave', 'Holiday']

/** Only stops a NEW day being created in a mode that never existed; `modeOptions` keeps existing ones rendering as themselves. */
export const SELECTABLE_MODES: string[] = WORK_MODES.filter(
  (m) => m !== 'L - Leave' && m !== 'Holiday' && m !== 'T - Transit',
)

// Source of truth: `PLAN_NEEDS_SIGNOFF` in `api/routers/time_tracking.py`; backend/tests/test_plan_signoff_literals.py pins the two.
/** ⛔ A client re-spelling of a server constant, kept here on purpose. */
export const PLAN_NEEDS_SIGNOFF: string[] = ['R - Remote', 'H - Home']

export type LinkedPlan = { status: string; mode: string }

/** Two questions, not one: `Approved` for an office day says nothing about working from home. */
export function remotePlanUnapproved(
  kind: LogKind,
  mode: string,
  plan: LinkedPlan | null,
): boolean {
  if (kind !== 'Actual') return false
  if (!PLAN_NEEDS_SIGNOFF.includes(mode)) return false
  if (plan === null) return false
  return !(plan.status === 'Approved' && PLAN_NEEDS_SIGNOFF.includes(plan.mode))
}

export function linkedPlanOf(day: DayLog, planRow: DayLog, daysLoaded: boolean): LinkedPlan | null {
  if (day.id) return { status: day.plan_status, mode: day.plan_mode }
  if (!daysLoaded) return null
  return planRow.id ? { status: planRow.status, mode: planRow.mode } : { status: '', mode: '' }
}

/** Composed here so the page reads as a question rather than as three. */
export function remotePlanWarning(day: DayLog, planRow: DayLog, daysLoaded: boolean): boolean {
  return remotePlanUnapproved(day.kind, day.mode, linkedPlanOf(day, planRow, daysLoaded))
}

export function modeOptions(dayMode: string, onRoster?: boolean): string[] {
  const base =
    onRoster === false ? SELECTABLE_MODES.filter((m) => m !== FIELD_MODE) : SELECTABLE_MODES
  return !dayMode || base.includes(dayMode) ? base : [...base, dayMode]
}

export const FIELD_MODE = 'F - Field'

/** Which statuses the admin dropdown may offer; a function because a current status that is not decidable joins the list as a non-offerable entry. */
export function statusOptions(
  current: LogStatus,
): Array<{ value: LogStatus; offerable: boolean }> {
  const decidable: LogStatus[] = ['Approved', 'Rejected']
  const offered = decidable.map((value) => ({ value, offerable: true }))
  return decidable.includes(current) ? offered : [{ value: current, offerable: false }, ...offered]
}

export function placeholderVisible(
  editable: boolean,
  ownDay: boolean,
  lineCount: number,
): boolean {
  return editable && ownDay && lineCount === 0
}

/** A plan submit must refuse a day with no lines. ⛔ Not fixed by widening `validateDay`. */
export function planHasNothingToSubmit(lines: ReadonlyArray<unknown>): boolean {
  return lines.length === 0
}

export const HOUR_FIELDS = [
  'hours',
  'overtime_hours',
  'nighttime_hours',
  'over_night_hours',
  'transit_hours',
] as const
export type HourField = (typeof HOUR_FIELDS)[number]

export const MARKETS = ['Mongolia', 'Kazakhstan', 'China', 'Other'] as const

const DEFAULT_MARKET = 'Mongolia'

export const DEFAULT_ACTIVITY = 'Tech & Ops'

export const DEFAULT_PRODUCT = 'Trigteq'

export const LEAVE_TYPES = [
  'AL - Annual Leave',
  'CL - Casual Leave',
  'CCL - Child Care Leave',
  'PSL - Paid Sick Leave',
  'SL - Sick Leave',
  'SPL - School Police Leave',
  'UL - Unpaid Leave',
  'ML - Maternity Leave',
  'PL - Paternity Leave',
  'RL - Roster Leave',
] as const

export const leaveCode = (type: string): string => type.split(' ')[0] ?? type

type BalanceKey = 'al_days' | 'cl_days' | 'psl_days' | 'spl_days'

export const LEAVE_BALANCE_OF: Record<string, BalanceKey> = {
  'AL - Annual Leave': 'al_days',
  'CL - Casual Leave': 'cl_days',
  'PSL - Paid Sick Leave': 'psl_days',
  'SPL - School Police Leave': 'spl_days',
}

/** Mirrors `NON_FULL_TIME_LEAVE_TYPES` in the time router, which enforces it; this only keeps the picker from offering what the server would refuse. */
const NON_FULL_TIME_LEAVE_TYPES: readonly string[] = ['UL - Unpaid Leave']

export function pickableLeaveTypes(isFullTime: boolean, _balance: ApiBalance | undefined): readonly string[] {
  return isFullTime ? LEAVE_TYPES : NON_FULL_TIME_LEAVE_TYPES
}

export function rlDisabledReason(balance: ApiBalance | undefined): string | null {
  return Number(balance?.rl_days ?? 0) > 0 ? null : 'leave.rlUnavailable'
}

/** The only five types with a half-day option; Annual, Maternity, Paternity, Roster and the business trips can never be split. */
export const HALF_DAY_TYPES: string[] = [
  'CL - Casual Leave',
  'SL - Sick Leave',
  'UL - Unpaid Leave',
  'PSL - Paid Sick Leave',
  'SPL - School Police Leave',
]

export type LogKind = 'Plan' | 'Actual'
export type LogStatus = 'Draft' | 'Pending' | 'Approved' | 'Rejected'

export type LeaveStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancel Pending' | 'Cancelled'

export const LEAVE_STATUSES: LeaveStatus[] = [
  'Pending',
  'Approved',
  'Rejected',
  'Cancel Pending',
  'Cancelled',
]

export type StageStatus =
  | ''
  | 'Pending'
  | 'Approved'
  | 'Rejected'
  | 'Cancel Approved'
  | 'Cancel Rejected'

export const APPROVAL_STAGES = ['supervisor', 'management', 'finance'] as const
type ApprovalStage = (typeof APPROVAL_STAGES)[number]

const STAGE_FIELD: Record<ApprovalStage, keyof LeaveApplication> = {
  supervisor: 'supervisor_status',
  management: 'management_status',
  finance: 'finance_status',
}

export const stageStatus = (app: LeaveApplication, stage: ApprovalStage): StageStatus =>
  (app[STAGE_FIELD[stage]] as StageStatus) ?? ''

let rowSeq = 0

export function emptyRow(activityId: string, productId: string | null = null): LogRow {
  rowSeq += 1
  return {
    id: `new-${rowSeq}`,
    market: DEFAULT_MARKET,
    activity_id: activityId,
    product_service_id: productId,
    shift: '',
    hours: 0,
    overtime_hours: 0,
    nighttime_hours: 0,
    over_night_hours: 0,
    transit_hours: 0,
    comment: '',
  }
}

export function blankDay(employeeId: string, day: string, kind: LogKind): DayLog {
  return {
    id: '',
    employee_id: employeeId,
    day,
    kind,
    mode: 'B - Office',
    status: 'Draft',
    rejection_reason: '',
    rejected_by: '',
    decided_by: '',
    decided_at: null,
    decided_actor_kind: '',
    // The editor offers `DEFAULT_CLOCK` behind a switch instead of starting there, so a day only
    // acquires a clock when somebody decides it should have one.
    start_of: null,
    end_of: null,
    gap: null,
    extra_hours: null,
    total: null,
    // Blank like every other unset field here, and the one place a blank plan means "nobody has asked"
    // rather than "no plan was filed".
    plan_status: '',
    plan_mode: '',
    lines: [],
  }
}

/** Matched rather than printed: this constant exists so the server's fixed phrase can be recognised. */
export const BEFORE_TRACKING = 'before tracking began'

/** What `decided_by`/`decided_at` mean together: four cases, each a true statement read as ordinary text, never a warning, dash or "Unknown". */
type DaySignature =
  | { kind: 'none' }
  | { kind: 'beforeTracking' }
  | { kind: 'system'; at: string | null }
  | { kind: 'actor'; name: string; at: string | null }

/** Pure, so the four cases are testable without a render harness. Deliberately ignores `day.status`. */
export function daySignature(day: DayLog): DaySignature {
  // `BEFORE_TRACKING` goes first: it is a fixed phrase from the server, not a person, and the actor
  // branch below would render it as somebody's name.
  if (day.decided_by === BEFORE_TRACKING) return { kind: 'beforeTracking' }
  if (day.decided_actor_kind === 'system') return { kind: 'system', at: day.decided_at }
  if (day.decided_by) return { kind: 'actor', name: day.decided_by, at: day.decided_at }
  return { kind: 'none' }
}

function rowTotal(row: LogRow): number {
  return HOUR_FIELDS.reduce((sum, field) => sum + Number(row[field] || 0), 0)
}

export function dayTotal(day: DayLog): number {
  return day.lines.reduce((sum, r) => sum + rowTotal(r), 0)
}

/** ⛔ Used only when somebody turns the clock on, never when a day loads. */
export const DEFAULT_CLOCK: DayClock = { start_of: 8, end_of: 17, gap: 1, extra_hours: 0 }

/** One null check, not five: `ck_time_day_clock_all_or_none` guarantees the five columns are never partly set. */
export function readClock(day: DayLog): DayClock | null {
  if (day.start_of === null || day.end_of === null || day.gap === null || day.extra_hours === null)
    return null
  return {
    start_of: Number(day.start_of),
    end_of: Number(day.end_of),
    gap: Number(day.gap),
    extra_hours: Number(day.extra_hours),
  }
}

export function clockTotal(clock: DayClock): number {
  return clock.end_of + clock.extra_hours - clock.start_of - clock.gap
}

/** ⛔ The courtesy copy of the server's check, toleranced: these are floats after JSON and `8.25 * 3` is not `24.75` in IEEE 754. */
export function clockAgrees(day: DayLog): boolean {
  const clock = readClock(day)
  if (!clock) return true
  return Math.abs(clockTotal(clock) - dayTotal(day)) < 0.005
}

/** Decimal hours to HH:MM; the storage and the input differ on purpose. */
export function hoursToTime(hours: number): string {
  const total = Math.round(hours * 60)
  const hh = Math.floor(total / 60)
  const mm = total % 60
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

/** ⛔ The rounding to a quarter hour is deliberate, not tidiness. */
export function timeToHours(value: string): number | null {
  if (!value) return null
  const [hh, mm] = value.split(':').map(Number)
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null
  return hh + Math.round(mm / 15) * 0.25
}

export const CLOCK_MINUTES = [0, 15, 30, 45] as const
export const CLOCK_HOURS = Array.from({ length: 24 }, (_, h) => h)

export const pad2 = (n: number): string => String(n).padStart(2, '0')

/** Snaps to the quarter and clamps to 00:00-23:45: a select cannot show a value it has no option for, 24 included. */
export function splitClockHours(hours: number): { hour: number; minute: number } {
  const quarters = Math.min(95, Math.max(0, Math.round(hours * 4)))
  return { hour: Math.floor(quarters / 4), minute: (quarters % 4) * 15 }
}

export function joinClockHours(hour: number, minute: number): number {
  return hour + minute / 60
}

/** ⛔ The quarter-hour rule `timeToHours` applies, for the two non-time inputs, so the browser cannot build an off-quarter request. */
export function toQuarter(hours: number): number {
  return Math.round(hours * 4) / 4
}

export function sumField(rows: LogRow[], field: HourField): number {
  return rows.reduce((sum, r) => sum + Number(r[field] || 0), 0)
}

/** A day may total the long cap only when more than the transit threshold is transit. */
export function dayTotalCap(transitHours: number, limits: DayLimits): number {
  return transitHours > limits.long_transit_over_hours
    ? limits.max_day_hours
    : limits.max_short_transit_day_hours
}

export function validateDay(
  rows: LogRow[],
  limits: DayLimits | undefined,
  downtimeIds: ReadonlySet<string> = new Set(),
  day?: string,
  productTypes?: ProductTypes,
): string[] {
  const errors: string[] = []
  if (rows.length === 0) return errors
  if (!limits) return ['time.errLimitsLoading']

  if (rows.some((r) => !r.market || !r.activity_id || !r.product_service_id))
    errors.push('time.errIncomplete')

  const keys = rows.map(
    (r) => `${r.market}|${r.activity_id}|${r.product_service_id}|${r.shift}|${r.work_mode ?? ''}`,
  )
  if (new Set(keys).size !== keys.length) errors.push('time.errDuplicate')

  if (rows.length > 1 && rows.some((r) => rowTotal(r) === 0)) errors.push('time.errEmptyRow')
  if (rows.some((r) => HOUR_FIELDS.some((f) => Number(r[f]) < 0))) errors.push('time.errNegative')

  const normal = sumField(rows, 'hours')
  const night = sumField(rows, 'nighttime_hours') + sumField(rows, 'over_night_hours')
  const overtime = sumField(rows, 'overtime_hours') + sumField(rows, 'over_night_hours')

  const transit = sumField(rows, 'transit_hours')
  if (rows.reduce((sum, r) => sum + rowTotal(r), 0) > dayTotalCap(transit, limits))
    errors.push(transit > limits.long_transit_over_hours ? 'time.errDayTotalLong' : 'time.errDayTotalShort')
  if (normal > limits.max_normal_hours) errors.push('time.errOrdinaryHours')
  if (transit > limits.max_transit_hours) errors.push('time.errTransitHours')
  if (night > limits.max_night_hours) errors.push('time.errNightHours')
  if (overtime > limits.max_overtime_hours) errors.push('time.errOvertimeHours')

  // By flag, not by name: the caller passes the ids of activities carrying `is_downtime`, so
  // renaming the activity cannot switch the cap off.
  if (rows.some((r) => downtimeIds.has(r.activity_id))) {
    if (downtimeCapApplies(day, limits)) {
      if (overtime > 0 || night > 0) errors.push('time.errDowntimeNoExtra')
    } else if (normal > limits.max_downtime_hours || overtime > 0 || night > 0) {
      errors.push('time.errDowntime')
    }
    if (productTypes && downtimeOffProject(rows, downtimeIds, productTypes))
      errors.push('time.errDowntimeProjectOnly')
  }

  return errors
}

export function downtimeCapApplies(day: string | undefined, limits: DayLimits): boolean {
  return !!day && day.slice(0, 10) >= limits.downtime_cap_from
}

export function dayIsOpen(
  date: string,
  kind: LogKind,
  status: LogStatus,
  window: DayWindow | undefined,
): boolean {
  if (!window) return false
  if (kind === 'Plan') return date >= window.today
  if (date > window.today || date < window.self_start) return false
  if (status === 'Rejected') return closedMonthOf(date, window) === undefined
  return date >= addDays(window.today, -window.open_window_days)
}

export function closedMonthOf(
  date: string,
  window: DayWindow | undefined,
): DayWindow['closed_months'][number] | undefined {
  return window?.closed_months.find((m) => m.start <= date && date <= m.end)
}

/** `submit_day` refuses a `Rejected` day in a closed payroll month for owner AND supervisor, so the supervisor's Resubmit must go too. */
export function resubmitLockedByMonthClose(
  status: LogStatus,
  date: string,
  window: DayWindow | undefined,
): boolean {
  if (status !== 'Rejected') return false
  return !window || closedMonthOf(date, window) !== undefined
}

/** ⛔ A control, so an unknown window reads as NO. */
export function ownRejectedDayResubmittable(
  kind: LogKind,
  status: LogStatus,
  date: string,
  window: DayWindow | undefined,
): boolean {
  if (!window || kind !== 'Actual' || status !== 'Rejected') return false
  if (date > window.today || date < window.self_start) return false
  return closedMonthOf(date, window) === undefined
}

/** `selectedDates` is what the calendar marks and the counter counts; `planDates` is what would actually submit. */
export function planSelection(
  kind: LogKind,
  current: readonly string[],
  next: string,
  today: string | undefined,
): string[] {
  if (kind !== 'Plan') return []
  if (!today) return [...current]
  const upcoming = current.filter((date) => date >= today)
  if (next < today) return upcoming
  return upcoming.includes(next)
    ? upcoming.filter((date) => date !== next)
    : [...upcoming, next]
}

export function awaitingStage(app: LeaveApplication, stage: ApprovalStage): boolean {
  const verdict = stageStatus(app, stage)
  if (!verdict) return false
  if (app.status === 'Pending') return verdict === 'Pending'
  if (app.status === 'Cancel Pending') return verdict === 'Approved' || verdict === 'Pending'
  return false
}

/** The `awaiting` scope's predicate: the server's per-row `awaits_me`, which `/api/dashboard/me` counts for the badge; unknown reads as not awaiting. */
export function awaitingMe(app: LeaveApplication): boolean {
  return app.awaits_me === true
}

/** `Approved` and `Cancel Pending` days are already off the stored balance; adding them here would charge them twice. */
export function unspentDays(
  apps: LeaveApplication[],
  employeeId: string,
  leaveType: string,
): number {
  return apps
    .filter(
      (a) =>
        a.employee_id === employeeId && a.leave_type === leaveType && a.status === 'Pending',
    )
    .reduce((total, a) => total + Number(a.days), 0)
}

export function peerOverlaps(
  apps: LeaveApplication[],
  employeeId: string,
  start: string,
  end: string,
): LeaveApplication[] {
  return apps.filter(
    (a) =>
      a.employee_id !== employeeId &&
      (a.status === 'Approved' || a.status === 'Pending' || a.status === 'Cancel Pending') &&
      a.start_date <= end &&
      start <= a.end_date,
  )
}

const PEER_BLOCK_EXEMPT_TYPES = ['SL - Sick Leave', 'PSL - Paid Sick Leave']

/** ⚠️ One direction only: who names the applicant as peer is not readable without `hr.view`, so that half is left to the server refusal. */
export function appointedPeerClashes(
  apps: LeaveApplication[],
  peerIds: string[],
  leaveType: string,
  start: string,
  end: string,
): LeaveApplication[] {
  if (PEER_BLOCK_EXEMPT_TYPES.includes(leaveType) || peerIds.length === 0) return []
  return apps
    .filter(
      (a) =>
        peerIds.includes(a.employee_id) &&
        (a.status === 'Approved' || a.status === 'Pending' || a.status === 'Cancel Pending') &&
        a.start_date <= end &&
        start <= a.end_date,
    )
    .sort((a, b) => a.start_date.localeCompare(b.start_date))
}

/** Display only: delegation (`standin.py`) keeps `Approved` alone, reading a withdrawal in flight as coming back. */
export const AWAY_STATUSES: readonly string[] = ['Approved', 'Cancel Pending']

export function isAwayOn(app: { status: string; start_date: string; end_date: string }, day: string): boolean {
  return AWAY_STATUSES.includes(app.status) && app.start_date <= day && day <= app.end_date
}

/** The statuses the server refuses a second request over (`LEAVE_LIVE_STATUSES`). */
export const LEAVE_LIVE_STATUSES: readonly string[] = ['Pending', 'Approved', 'Cancel Pending']

/** The applicant's own leave touching the requested dates; an unranged list is the newest page company-wide. */
export function ownLeaveWindowParams(start: string, end: string) {
  return { scope: 'own', from: start, to: end, limit: 200 } as const
}

/** The applicant's own undecided leave, filtered by the server so no page limit can drop a Pending row. */
export function ownAwaitingLeaveParams() {
  return { scope: 'own', status: 'awaiting', limit: 200 } as const
}

/** Everyone's leave touching the requested dates; null without staff, whom the server refuses other people's leave. */
export function peerWindowParams(start: string, end: string, mayReadPeers: boolean) {
  return mayReadPeers ? ({ scope: 'all', from: start, to: end, limit: 200 } as const) : null
}

/** The applicant's own live leave sharing any requested date, half-days included: what `refuse_if_leave_overlaps` 409s. */
export function selfOverlaps(
  apps: LeaveApplication[],
  employeeId: string,
  start: string,
  end: string,
): LeaveApplication[] {
  return apps.filter(
    (a) =>
      a.employee_id === employeeId &&
      LEAVE_LIVE_STATUSES.includes(a.status) &&
      a.start_date <= end &&
      start <= a.end_date,
  )
}

export function leaveOn(
  apps: LeaveApplication[],
  employeeId: string,
  date: string,
  statuses: readonly string[] = AWAY_STATUSES,
): LeaveApplication | undefined {
  return apps.find(
    (a) =>
      a.employee_id === employeeId &&
      statuses.includes(a.status) &&
      a.start_date <= date &&
      date <= a.end_date,
  )
}

export function statusTone(status: string): string {
  switch (status) {
    case 'Approved':
      return 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
    case 'Pending':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
    case 'Rejected':
      return 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
    case 'Cancel Pending':
      return 'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300'
    case 'Cancelled':
    case 'Cancel Approved':
      return 'bg-slate-100 text-slate-500 dark:bg-slate-700/60 dark:text-slate-400'
    default:
      return 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300'
  }
}

export function modeTone(mode: string): string {
  switch (mode) {
    case 'B - Office':
      return 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
    case 'H - Home':
      return 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300'
    case 'R - Remote':
      return 'bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300'
    case 'F - Field':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
    case 'T - Transit':
      return 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300'
    case 'L - Leave':
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
    case 'Holiday':
      return 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300'
    default:
      return 'bg-slate-100 text-slate-500 dark:bg-slate-700/60 dark:text-slate-400'
  }
}

/** ⚠️ The shade is the point, not a preference; one function keeps the callers from drifting apart. */
export function modeFill(mode: string | undefined): string {
  switch (mode) {
    case 'B - Office':
      return 'bg-blue-100 dark:bg-blue-900/30'
    case 'H - Home':
    case 'R - Remote':
      return 'bg-teal-200 dark:bg-teal-800/50'
    case 'F - Field':
    case 'T - Transit':
      return 'bg-amber-200 dark:bg-amber-800/50'
    case 'BTA - Business Trip Abroad':
    case 'BTL - Business Trip Local':
      return 'bg-violet-200 dark:bg-violet-800/50'
    case 'O - Off':
      return 'bg-slate-200 dark:bg-slate-700/60'
    case 'Holiday':
      return 'bg-rose-200 dark:bg-rose-800/50'
    case 'L - Leave':
      return 'bg-emerald-200 dark:bg-emerald-800/50'
    default:
      return ''
  }
}

/** Outside the shown month only a holiday keeps a fill, lighter than in-month; the dot carries its kind. */
export function cellFill(
  outside: boolean,
  holidayKind: string | null | undefined,
  mode: string | undefined,
): string {
  if (!outside) return modeFill(mode)
  return holidayKind ? 'bg-rose-100 dark:bg-rose-900/30' : 'bg-slate-50 dark:bg-slate-900/40'
}

export function leaveTone(type: string): string {
  switch (type) {
    case 'AL - Annual Leave':
      return 'bg-orange-200 text-orange-900 dark:bg-orange-800/70 dark:text-orange-50'
    case 'SL - Sick Leave':
      return 'bg-blue-200 text-blue-900 dark:bg-blue-800/70 dark:text-blue-50'
    case 'PSL - Paid Sick Leave':
      return 'bg-red-200 text-red-900 dark:bg-red-800/70 dark:text-red-50'
    case 'CL - Casual Leave':
      return 'bg-green-200 text-green-900 dark:bg-green-800/70 dark:text-green-50'
    case 'ML - Maternity Leave':
      return 'bg-sky-200 text-sky-900 dark:bg-sky-800/70 dark:text-sky-50'
    case 'PL - Paternity Leave':
      return 'bg-purple-200 text-purple-900 dark:bg-purple-800/70 dark:text-purple-50'
    case 'UL - Unpaid Leave':
      return 'bg-yellow-100 text-yellow-900 dark:bg-yellow-800/70 dark:text-yellow-50'
    case 'SPL - School Police Leave':
      return 'bg-cyan-100 text-cyan-900 dark:bg-cyan-800/70 dark:text-cyan-50'
    case 'RL - Roster Leave':
      return 'bg-amber-300 text-amber-950 dark:bg-amber-700/70 dark:text-amber-50'
    case 'CCL - Child Care Leave':
      return 'bg-teal-200 text-teal-900 dark:bg-teal-800/70 dark:text-teal-50'
    case 'BTL - Business Trip Local':
    case 'BTA - Business Trip Abroad':
      return 'bg-violet-200 text-violet-900 dark:bg-violet-800/70 dark:text-violet-50'
    default:
      return 'bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-50'
  }
}

export const COSIGN_STAGES: readonly CosignStage[] = ['supervisor', 'ceo', 'cfo']

export type CosignLines = Record<CosignStage, CosignVerdict>

/** What the page must say and offer about co-signature for one day. */
export type CosignState =
  | { kind: 'none' | 'unknown' | 'needed' | 'paused' | 'beyond' | 'final' }
  | { kind: 'sendFrom'; from: string }
  | { kind: 'awaiting' | 'approved' | 'rejected'; lines: CosignLines }

function cosignLines(day: ApiDay): CosignLines {
  return {
    supervisor: day.cosign_supervisor ?? 'Pending',
    ceo: day.cosign_ceo ?? 'Pending',
    cfo: day.cosign_cfo ?? 'Pending',
  }
}

export type CosignSignerCount = 1 | 2 | 3

export function cosignSignerCount(
  state: CosignState,
  window: DayWindow | undefined,
): CosignSignerCount {
  if ('lines' in state) {
    const lines = state.lines
    const live = COSIGN_STAGES.filter((stage) => lines[stage] !== 'Waived').length
    return live <= 1 ? 1 : live === 2 ? 2 : 3
  }
  if (state.kind !== 'needed') return 3
  if (window?.may_backdate === true) return 1
  return window?.cosign_supervisor_waived === true ? 2 : 3
}

export function cosignSignerSuffix(count: CosignSignerCount): '' | 'Two' | 'One' {
  return count === 1 ? 'One' : count === 2 ? 'Two' : ''
}

/** ⛔ A control, so an unknown reads as NO: no window yet, or a day below `cosign_floor` (the request has expired). */
export function cosignReversibleStage(
  day: ApiDay,
  date: string,
  viewerId: string | undefined,
  window: DayWindow | undefined,
): CosignStage | null {
  if (!day.cosign_requested_at || !day.cosign_closed_at || day.status !== 'Rejected') return null
  if (!viewerId || !day.rejected_by_id || day.rejected_by_id !== viewerId) return null
  if (!window || !window.cosign_floor || date < window.cosign_floor) return null
  const lines = cosignLines(day)
  const refused = COSIGN_STAGES.filter((stage) => lines[stage] === 'Rejected')
  return refused.length === 1 ? refused[0] : null
}

/** Re-applies `cosignReversibleStage` so an unknown viewer or day window shows NOTHING rather than a button the route may refuse. */
export function refusalsToReverse<T extends ApiDay>(
  requests: readonly T[] | undefined,
  viewerId: string | undefined,
  window: DayWindow | undefined,
): { request: T; stage: CosignStage }[] {
  const out: { request: T; stage: CosignStage }[] = []
  for (const request of requests ?? []) {
    const stage = cosignReversibleStage(request, request.day, viewerId, window)
    if (stage) out.push({ request, stage })
  }
  return out
}

export function cosignState(
  day: ApiDay,
  date: string,
  kind: LogKind,
  window: DayWindow | undefined,
): CosignState {
  if (day.cosign_requested_at) {
    const lines = cosignLines(day)
    if (!day.cosign_closed_at && day.status === 'Pending') return { kind: 'awaiting', lines }
    return { kind: day.status === 'Approved' ? 'approved' : 'rejected', lines }
  }
  if (kind !== 'Actual') return { kind: 'none' }
  if (!window) return { kind: 'unknown' }
  if (date >= window.self_start || date > window.today) return { kind: 'none' }
  if (day.status === 'Pending' || day.status === 'Approved') return { kind: 'none' }
  if (day.status === 'Rejected' && day.rejected_by === '') return { kind: 'final' }
  if (!window.cosign_floor) return { kind: 'unknown' }
  if (date < window.cosign_floor) return { kind: 'beyond' }
  if (window.cosign_requests_enabled === undefined) return { kind: 'unknown' }
  if (!window.cosign_requests_enabled) return { kind: 'paused' }
  return { kind: 'needed' }
}

/** A 'needed' day under the pay-date lock cannot be sent today, so it says from when it can; an unknown window says nothing. */
export function lockedCosignState(
  state: CosignState,
  locked: boolean,
  window: DayWindow | undefined,
): CosignState {
  if (state.kind !== 'needed' || !locked || !window) return state
  return { kind: 'sendFrom', from: addDays(window.today, 1) }
}

export interface LateEditProposer {
  timeAdmin: boolean
  ceo: boolean
  cfo: boolean
  pastEdit: boolean
  supervisesSubject: boolean
}

/** Who the server lets open a request on someone else's older day; a covering stand-in included. */
export function mayProposeLateEdit(viewer: LateEditProposer): boolean {
  return viewer.timeAdmin || viewer.ceo || viewer.cfo || viewer.pastEdit || viewer.supervisesSubject
}

/** ⛔ A control, so an unknown reads as NO: whether the viewer may request on someone else's older Actual day (`cosign_required`). */
export function lateEditProposable(input: {
  mayPropose: boolean
  viewerId: string | undefined
  subjectId: string | undefined
  day: ApiDay
  date: string
  kind: LogKind
  window: DayWindow | undefined
}): boolean {
  const { day, date, window } = input
  if (!input.mayPropose || input.kind !== 'Actual') return false
  if (!input.viewerId || !input.subjectId || input.viewerId === input.subjectId) return false
  if (!window || !window.cosign_floor || window.cosign_requests_enabled !== true) return false
  if (date >= window.self_start || date > window.today || date < window.cosign_floor) return false
  if (day.cosign_requested_at && !day.cosign_closed_at && day.status === 'Pending') return false
  if (day.status === 'Rejected' && day.rejected_by === '') return false
  return true
}

export function closedMonthCosignNote(
  date: string,
  window: DayWindow | undefined,
): { month: string } | null {
  const closed = closedMonthOf(date, window)
  if (!closed || !Number.isInteger(closed.year) || !Number.isInteger(closed.month)) return null
  if (closed.month < 1 || closed.month > 12) return null
  return { month: `${closed.year}-${String(closed.month).padStart(2, '0')}` }
}

type CosignFailure =
  | { kind: 'paused' | 'required' }
  | { kind: 'monthClosed'; text: string | null; month: string | null }
export function cosignFailure(detail: unknown): CosignFailure | null {
  if (typeof detail !== 'object' || detail === null) return null
  const d = detail as Record<string, unknown>
  if (d.code === 'cosign_paused') return { kind: 'paused' }
  if (d.code === COSIGN_REQUIRED) return { kind: 'required' }
  if (d.code !== 'payroll_month_closed') return null
  const withReason = typeof d.reason === 'string' && d.reason !== ''
  const message = typeof d.message === 'string' && d.message !== '' ? d.message : null
  const warn = typeof d.warn === 'string' && d.warn !== '' ? d.warn : null
  const text = withReason && message ? (warn ? `${message} ${warn}` : message) : null
  const month =
    typeof d.year === 'number' && typeof d.month === 'number' && d.month >= 1 && d.month <= 12
      ? `${d.year}-${String(d.month).padStart(2, '0')}`
      : null
  return { kind: 'monthClosed', text, month }
}

export function isCosignPaused(detail: unknown): boolean {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    (detail as { code?: unknown }).code === 'cosign_paused'
  )
}

export function isCosignRequired(detail: unknown): boolean {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    (detail as { code?: unknown }).code === COSIGN_REQUIRED
  )
}

export function isAlreadyApproved(detail: unknown): boolean {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    (detail as { code?: unknown }).code === ALREADY_APPROVED
  )
}

export function submitWouldReopenApproval(dirty: boolean, storedStatus: string | undefined): boolean {
  return !dirty && storedStatus === 'Approved'
}
